import React, { useEffect, useState } from "react";
import { Play, Loader2, Beaker, Thermometer, Zap, Droplets, Activity, FileText } from "lucide-react";
import api, { formatErr } from "../lib/api";
import SimulationV3Panel from "./SimulationV3Panel";
import AutoSimWorkspace from "./AutoSimWorkspace";

const CASES = [
  { id: "normal", label: "Normal" },
  { id: "max",    label: "Max 110%" },
  { id: "min",    label: "Min 60%" },
  { id: "summer", label: "Summer" },
  { id: "winter", label: "Winter" },
];

export default function SimulationViewer({ projectId }) {
  const [view, setView] = useState("v3"); // v3 = default Engine v3 calculator suite
  const [util, setUtil] = useState(null);
  const [hmb, setHmb] = useState(null);
  const [equipment, setEquipment] = useState(null);
  const [caseId, setCaseId] = useState("normal");
  const [running, setRunning] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [autoFillSource, setAutoFillSource] = useState(null); // "design_basis" | "empty" | null
  const [err, setErr] = useState("");

  // Wave Z — auto-construct simulation prompt from PDO design_basis the
  // first time the Project Auto-Sim tab is opened (or when the PDO
  // version changes). The user can still edit before running.
  const loadAutoPrompt = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/simulation/auto-prompt`);
      setAutoFillSource(r.data.source);
      // Only overwrite the prompt if the user hasn't typed anything yet.
      setPrompt((cur) => (cur && cur.length > 0 ? cur : (r.data.prompt || "")));
    } catch { /* ignore — fall back to manual entry */ }
  };

  const load = async () => {
    setErr("");
    try {
      const u = await api.get(`/projects/${projectId}/simulation/utility-summary`);
      setUtil(u.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setUtil(null); setHmb(null); setEquipment(null);
      } else {
        setErr(formatErr(e.response?.data?.detail) || e.message);
      }
    }
  };

  const loadCase = async () => {
    if (!util) return;
    try {
      const [h, e] = await Promise.all([
        api.get(`/projects/${projectId}/simulation/hmb?case_id=${caseId}`),
        api.get(`/projects/${projectId}/simulation/equipment?case_id=${caseId}`),
      ]);
      setHmb(h.data.case);
      setEquipment(e.data.equipment);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); loadAutoPrompt(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => { loadCase(); /* eslint-disable-next-line */ }, [util, caseId]);
  useEffect(() => {
    // Re-fetch auto-prompt when user switches into the Project Auto-Sim
    // sub-tab (PDO may have changed since last visit).
    if (view === "project") loadAutoPrompt();
    // eslint-disable-next-line
  }, [view]);

  // Poll while running
  useEffect(() => {
    if (!running) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/stages/B.1/last-run`);
        if (data.status === "complete" || data.status === "blocked") {
          setRunning(false);
          await load();
          clearInterval(id);
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(id);
  }, [running, projectId]);

  const triggerSim = async () => {
    setErr(""); setRunning(true);
    try {
      await api.post(`/projects/${projectId}/simulate`, { prompt });
    } catch (e) {
      setRunning(false);
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="simulation-viewer">
      {/* Sub-tabs: project auto-sim vs Engine v3 */}
      <div style={{ display: "flex", gap: 4, padding: 4, marginBottom: 14, background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", width: "fit-content" }}>
        <button
          data-testid="sim-tab-project"
          className="btn btn-ghost"
          onClick={() => setView("project")}
          style={{ padding: "6px 14px", borderColor: view === "project" ? "var(--pf-accent)" : "transparent", color: view === "project" ? "var(--pf-accent)" : "var(--pf-text)" }}
        >
          <Activity size={13}/> Project Auto-Sim
        </button>
        <button
          data-testid="sim-tab-v3"
          className="btn btn-ghost"
          onClick={() => setView("v3")}
          style={{ padding: "6px 14px", borderColor: view === "v3" ? "var(--pf-accent)" : "transparent", color: view === "v3" ? "var(--pf-accent)" : "var(--pf-text)" }}
        >
          <Beaker size={13}/> Engine v3 — Calculator Suite
        </button>
      </div>

      {view === "v3" ? (
        <div style={{ height: "calc(100vh - 240px)" }}>
          <SimulationV3Panel projectId={projectId} />
        </div>
      ) : view === "project" ? (
        <AutoSimWorkspace projectId={projectId} />
      ) : (<>
      <div className="pf-overline" style={{ marginBottom: 6 }}>PROCESS SIMULATION · MODULE O</div>
      <h2 className="pf-heading" style={{ fontSize: 22, margin: "0 0 14px" }}>Heat &amp; Mass Balance — first-principles engine</h2>

      {err && <div className="pf-sev pf-sev-error" style={{ display: "block", padding: "8px 10px", marginBottom: 12 }} data-testid="sim-error">{err}</div>}

      {/* Wave Z — auto-fill banner */}
      {autoFillSource === "design_basis" && (
        <div data-testid="autosim-banner-filled" style={{
          padding: "8px 14px", marginBottom: 12, borderRadius: 4,
          background: "rgba(34,197,94,0.10)", border: "1px solid #22c55e",
          color: "#86efac", fontSize: 12, display: "flex", alignItems: "center", gap: 8,
        }}>
          <Activity size={13} />
          Simulation parameters pre-filled from Design Basis (Stage A.1-A.5). Review and click Run Simulation.
        </div>
      )}
      {autoFillSource === "empty" && (
        <div data-testid="autosim-banner-empty" style={{
          padding: "8px 14px", marginBottom: 12, borderRadius: 4,
          background: "rgba(234,179,8,0.10)", border: "1px solid #eab308",
          color: "#fde68a", fontSize: 12, display: "flex", alignItems: "center", gap: 8,
        }}>
          <Activity size={13} />
          Complete Stage A (Design Basis) first for auto-populated simulation, or enter parameters manually below.
        </div>
      )}

      {/* Trigger panel */}
      <div className="pf-card" style={{ padding: 14, marginBottom: 18 }}>
        <div className="pf-overline" style={{ marginBottom: 6 }}>RUN SIMULATION</div>
        <textarea
          className="pf-input pf-textarea"
          rows={3}
          placeholder='e.g. "Reformate splitter T-101: 62500 kgph feed at 5 bar / 90 °C, 30% benzene 25% toluene 25% n-heptane 20% MCP. Recover 99% benzene overhead, 99% toluene bottoms."'
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={running}
          data-testid="sim-prompt"
        />
        <div style={{ marginTop: 8, display: "flex", gap: 10 }}>
          <button className="btn btn-primary" onClick={triggerSim} disabled={running} data-testid="sim-run">
            {running ? <><Loader2 className="pf-spin" size={14} /> Running…</> : <><Play size={14} /> Run simulation</>}
          </button>
          <span style={{ fontSize: 11, color: "var(--pf-text-muted)", alignSelf: "center" }}>
            Engine: PR EOS · Rachford-Rice · IAPWS-IF97 · Fenske-Underwood-Gilliland
          </span>
        </div>
      </div>

      {!util ? (
        <div style={{ padding: 26, color: "var(--pf-text-muted)", textAlign: "center", border: "1px dashed var(--pf-border)", borderRadius: 4 }}>
          No simulation results yet. Describe your flowsheet above and run.
        </div>
      ) : (
        <>
          {/* Worst-case envelope tiles */}
          <div className="pf-overline" style={{ marginBottom: 8 }}>WORST-CASE ENVELOPE (FOR SIZING)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 10, marginBottom: 22 }}>
            <Tile icon={Thermometer} label="Heating" value={`${util.worst_case_envelope.heating_duty_kW} kW`} />
            <Tile icon={Droplets}    label="Cooling" value={`${util.worst_case_envelope.cooling_duty_kW} kW`} />
            <Tile icon={Zap}         label="Electrical" value={`${util.worst_case_envelope.electrical_kW} kW`} />
            <Tile icon={Beaker}      label="MP Steam" value={`${util.worst_case_envelope.MP_steam_kgph} kg/h`} />
            <Tile icon={Droplets}    label="Cooling Water" value={`${util.worst_case_envelope.cooling_water_m3ph} m³/h`} />
          </div>

          {/* Energy Sankey */}
          <div className="pf-overline" style={{ marginBottom: 8 }}>ENERGY FLOW · SANKEY</div>
          <EnergySankey envelope={util.worst_case_envelope} />

          {/* Case selector */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {CASES.map((c) => (
              <button
                key={c.id}
                className="btn btn-ghost"
                onClick={() => setCaseId(c.id)}
                data-testid={`sim-case-${c.id}`}
                style={{ borderColor: caseId === c.id ? "var(--pf-spark)" : "var(--pf-border)", color: caseId === c.id ? "var(--pf-spark)" : "var(--pf-text-muted)" }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* H&MB grid */}
          {hmb && (
            <div style={{ marginBottom: 22 }}>
              <div className="pf-overline" style={{ marginBottom: 6 }}>HEAT &amp; MASS BALANCE — {hmb.case_name.toUpperCase()}</div>
              <div className="pf-card" style={{ padding: 0, overflow: "auto" }}>
                <HMBTable rows={hmb.streams} />
              </div>
            </div>
          )}

          {/* Equipment list */}
          {equipment && (
            <div style={{ marginBottom: 22 }}>
              <div className="pf-overline" style={{ marginBottom: 6 }}>EQUIPMENT — {hmb?.case_name?.toUpperCase()}</div>
              <div className="pf-card" style={{ padding: 0, overflow: "auto" }}>
                <table className="pf-table" data-testid="sim-equipment-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "var(--pf-surface-2)" }}>
                      <th style={{ padding: 8, textAlign: "left", borderBottom: "1px solid var(--pf-border)" }}>TAG</th>
                      <th style={{ padding: 8, textAlign: "left", borderBottom: "1px solid var(--pf-border)" }}>TYPE</th>
                      <th style={{ padding: 8, textAlign: "left", borderBottom: "1px solid var(--pf-border)" }}>KEY PARAMETERS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipment.map((e, i) => (
                      <tr key={i}>
                        <td style={{ padding: 8, borderBottom: "1px solid var(--pf-border)", fontFamily: "'JetBrains Mono', monospace", color: "var(--pf-spark)" }}>{e.unit}</td>
                        <td style={{ padding: 8, borderBottom: "1px solid var(--pf-border)" }}>{e.type || "—"}</td>
                        <td style={{ padding: 8, borderBottom: "1px solid var(--pf-border)", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                          {Object.entries(e).filter(([k]) => !["unit", "type", "case_factor"].includes(k)).map(([k, v]) => `${k}=${v}`).join("  ·  ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All-case utility comparison */}
          <div className="pf-overline" style={{ marginBottom: 6 }}>UTILITY COMPARISON ACROSS CASES</div>
          <div className="pf-card" style={{ padding: 0, overflow: "auto" }}>
            <table className="pf-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--pf-surface-2)" }}>
                  <th style={cellHead}>CASE</th>
                  <th style={cellHead}>HEATING kW</th>
                  <th style={cellHead}>COOLING kW</th>
                  <th style={cellHead}>POWER kW</th>
                  <th style={cellHead}>MP STEAM kg/h</th>
                  <th style={cellHead}>CW m³/h</th>
                </tr>
              </thead>
              <tbody>
                {util.cases.map((c) => (
                  <tr key={c.case_id} data-testid={`util-row-${c.case_id}`}>
                    <td style={cell}><strong>{c.case_id.toUpperCase()}</strong></td>
                    <td style={cell}>{c.utilities.heating_duty_kW}</td>
                    <td style={cell}>{c.utilities.cooling_duty_kW}</td>
                    <td style={cell}>{c.utilities.electrical_kW}</td>
                    <td style={cell}>{c.utilities.MP_steam_kgph}</td>
                    <td style={cell}>{c.utilities.cooling_water_m3ph}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>)}
    </div>
  );
}

const cell     = { padding: 8, borderBottom: "1px solid var(--pf-border)", fontFamily: "'JetBrains Mono', monospace" };
const cellHead = { ...cell, textAlign: "left", color: "var(--pf-text-muted)", fontSize: 11, letterSpacing: "0.05em" };

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="pf-card" style={{ padding: 14, display: "flex", alignItems: "center", gap: 10 }}>
      <Icon size={20} color="var(--pf-spark)" />
      <div>
        <div style={{ fontSize: 11, color: "var(--pf-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
        <div className="pf-heading" style={{ fontSize: 16, margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
      </div>
    </div>
  );
}

function HMBTable({ rows }) {
  if (!rows || rows.length === 0) return <div style={{ padding: 12, color: "var(--pf-text-muted)" }}>No streams.</div>;
  const baseCols = ["stream", "description", "T_C", "P_barg", "flow_kgph", "MW", "phase", "vapor_fraction", "molar_flow_kmolph"];
  const compCols = Array.from(new Set(rows.flatMap((r) => Object.keys(r).filter((k) => k.startsWith("x_"))))).sort();
  const cols = [...baseCols, ...compCols];
  return (
    <table className="pf-table" data-testid="sim-hmb-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
      <thead>
        <tr style={{ background: "var(--pf-surface-2)" }}>
          {cols.map((c) => <th key={c} style={cellHead}>{c.replace(/_/g, " ").toUpperCase()}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {cols.map((c) => (
              <td key={c} style={cell}>{r[c] !== undefined && r[c] !== null ? r[c] : "—"}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function EnergySankey({ envelope }) {
  // Build a flow set from the envelope: utility sources → process sinks.
  const heating = Number(envelope?.heating_duty_kW || 0);
  const cooling = Number(envelope?.cooling_duty_kW || 0);
  const elec = Number(envelope?.electrical_kW || 0);
  const total = heating + cooling + elec || 1;
  const flows = [
    { from: "MP Steam", to: "Reboilers / Heaters", value: heating, label: `${heating.toFixed(0)} kW` },
    { from: "Cooling Water", to: "Condensers / Coolers", value: cooling, label: `${cooling.toFixed(0)} kW` },
    { from: "Grid (Electrical)", to: "Pumps + Compressors", value: elec, label: `${elec.toFixed(0)} kW` },
  ].filter((f) => f.value > 0);

  if (flows.length === 0) return <div style={{ color: "var(--pf-text-muted)", fontSize: 12, marginBottom: 22 }}>No utility flows to plot.</div>;

  // Inline sankey rendering (small JSX version of drawings.sankey_svg)
  const W = 1100, H = 320;
  const yTop = 40;
  const chartH = H - 80;
  const px = chartH / total;
  let lY = yTop, rY = yTop;
  const items = [];
  flows.forEach((f, i) => {
    const h = f.value * px;
    items.push(
      <g key={i}>
        <rect x="160" y={lY} width="20" height={h} fill="var(--pf-accent)" />
        <text x="155" y={lY + h / 2 + 4} textAnchor="end" fontSize="11" fill="var(--pf-text)">{f.from}</text>
        <rect x={W - 180} y={rY} width="20" height={h} fill="var(--pf-spark)" />
        <text x={W - 150} y={rY + h / 2 + 4} fontSize="11" fill="var(--pf-text)">{f.to}</text>
        <path d={`M 180 ${lY} C ${W/2} ${lY}, ${W/2} ${rY}, ${W-180} ${rY} L ${W-180} ${rY+h} C ${W/2} ${rY+h}, ${W/2} ${lY+h}, 180 ${lY+h} Z`}
              fill="var(--pf-accent)" fillOpacity="0.18" stroke="var(--pf-accent)" strokeWidth="0.5" />
        <text x={W/2} y={(lY+rY)/2 + h/2} textAnchor="middle" fontSize="10" fill="var(--pf-text)">{f.label}</text>
      </g>
    );
    lY += h + 6;
    rY += h + 6;
  });
  return (
    <div className="pf-card" style={{ padding: 12, marginBottom: 22 }} data-testid="sim-sankey">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>{items}</svg>
    </div>
  );
}
